<template>
  <div class="grid gap-5">
    <!-- Main sales chart -->
    <SalesCard
      :title="$t('report.sales_comparison.sales_history')"
      :show-chart="true"
      :center="centerId"
      :component="srId"
      :cumulated="false"
      :show-evolution="false"
      :hide-edit="true"
      :x-axis-formatter="formatXLabel"
      :seasonality-data="seasonalityData"
    >
      <template #toolbar>
        <div class="flex justify-center">
          <SegmentedPicker
            v-model="aggregation"
            :groups="pickerGroups"
          />
        </div>
      </template>
    </SalesCard>

    <!-- Top 5 + Concentration analysis side by side -->
    <div v-if="productsReport" class="grid grid-cols-2 gap-5">
      <ReportCard :title="$t('center_sales_report.top_performances')">
        <Top5Products :products-report="productsReport" :center="center" />
      </ReportCard>

      <ReportCard :title="$t('center_sales_report.risks_analysis')">
        <TrendingInsights :products-report="productsReport" :center="center" />
      </ReportCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReportContext } from '../../../composables/useReportContext'
import { useTimeAggregation } from '../../../composables/useTimeAggregation'
import { useAppSettingsStore } from '../../../stores/AppSettingsStore'
import { SalesApi } from '../../../api/SalesApi'
import SalesCard from '../../SalesCard.vue'
import ReportCard from '../../ReportCard.vue'
import Top5Products from '../../Top5Products.vue'
import TrendingInsights from '../../TrendingInsights.vue'
import SegmentedPicker, { type PickerGroup } from '../../ui/SegmentedPicker.vue'
import type { Center } from '../../../user'

const { t } = useI18n()
const { card, centerId, srId } = useReportContext()
const appSettingsStore = useAppSettingsStore()

const center = computed<Center>(() => {
  const c = appSettingsStore.getCenter(centerId)
  if (!c) {
    throw new Error('SalesTab: center not found for centerId=' + centerId)
  }
  return c
})

const productsReport = computed(() => {
  return card.value?.productsReport ?? null
})

const seasonalityData = ref<number[] | undefined>(undefined)

// --- Time aggregation ---
const startDate = computed(() => card.value?.date?.startDate?.toJSDate() ?? new Date())
const endDate = computed(() => card.value?.date?.endDate?.toJSDate() ?? new Date())
const { aggregation, aggregationOptions, formatXLabel } = useTimeAggregation(startDate, endDate)

const pickerGroups = computed<PickerGroup[]>(() => {
  const chronological = aggregationOptions.value.filter(o => o.group === 'chronological')
  const distribution = aggregationOptions.value.filter(o => o.group === 'distribution')
  const groups: PickerGroup[] = []
  if (chronological.length) {
    groups.push({
      label: t('time_aggregation.group_chronological'),
      options: chronological.map(o => ({ value: o.value, label: o.label })),
    })
  }
  if (distribution.length) {
    groups.push({
      label: t('time_aggregation.group_distribution'),
      options: distribution.map(o => ({ value: o.value, label: o.label })),
    })
  }
  return groups
})

// Restore aggregation from model if already set
const restoring = ref(false)
if (card.value?.salesAggregation) {
  restoring.value = true
  aggregation.value = card.value.salesAggregation as typeof aggregation.value
  setTimeout(() => { restoring.value = false }, 0)
}

async function fetchSeasonalityData(intervalOverride?: string) {
  if (!card.value) return
  try {
    const deposit = card.value.dataType === 'deposit'
    const n1Start = card.value.date.startDate.minus({ years: 1 })
    const n1End = card.value.date.endDate.minus({ years: 1 })
    const interval = (intervalOverride ?? card.value.interval) as Parameters<typeof SalesApi.fetchSalesByInterval>[2]

    const resp = await SalesApi.fetchSalesByInterval(
      n1Start, n1End,
      interval, card.value.dataOrigin, deposit, centerId
    )

    const taxKey = card.value.getTaxKey()
    seasonalityData.value = resp.intervals.map(
      i => i.data.cumulative.tax_data[taxKey]
    )
  } catch {
    seasonalityData.value = undefined
  }
}

// Watch aggregation changes and re-fetch
watch(aggregation, async (newAgg) => {
  if (!card.value || restoring.value) return
  card.value.data = null
  try {
    await Promise.all([
      card.value.fetchData(centerId, true, newAgg),
      fetchSeasonalityData(newAgg),
    ])
  } catch {
    // errors handled inside fetchData
  }
})

// Fetch seasonality on mount
fetchSeasonalityData(aggregation.value).catch(() => {})

// Save aggregation when leaving, but do NOT clear card.data (desktop keeps it cached)
onBeforeUnmount(() => {
  if (!card.value) return
  card.value.salesAggregation = aggregation.value
})
</script>
